import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, Eye } from "lucide-react";
import Logo from "@/components/Logo";

// Zentrale Navigation – NUR real existierende Ziele. Fehlende Seiten (Blog,
// Leitfäden, Webinare, Hilfe-Center, Über uns, Karriere, Presse, Kontakt,
// Integrationen, Status) sind bewusst entfernt.
export const NAV = {
  Produkt: [
    { label: "So funktioniert NEWZER", to: "/so-funktioniert-newzer" },
    { label: "Frühwarnsystem", to: "/fruehwarnsystem" },
    { label: "Medienbeobachtung", to: "/medienmonitoring" },
    { label: "Krisenfrüherkennung", to: "/krisenmonitoring" },
    { label: "RSS-Monitoring", to: "/rss-monitoring" },
    { label: "Quellen", to: "/quellen" },
    { label: "Live-Demo", to: "/demo" },
  ],
  "Lösungen": [
    { label: "Unternehmen", to: "/loesungen/unternehmen" },
    { label: "Kommunen & öffentliche Kommunikation", to: "/loesungen/kommunen" },
    { label: "Personen & Reputation", to: "/loesungen/personen" },
    { label: "Reputationsmonitoring", to: "/reputationsmanagement" },
    { label: "Agenturen & PR", to: "/loesungen/agenturen" },
  ],
  "Vergleich": [
    { label: "Google Alerts Alternative", to: "/google-alerts-alternative" },
  ],
};

export function isHash(to) { return to.startsWith("/#"); }
export function NavItem({ to, children, className, ...rest }) {
  if (isHash(to)) return <a href={to.slice(1)} className={className} {...rest}>{children}</a>;
  return <Link to={to} className={className} {...rest}>{children}</Link>;
}

// Reduzierter Marken-Header für Auth-Seiten: Logo + Start/Datenschutz/Impressum.
function MinimalHeader() {
  return (
    <header className="border-b border-fc-border bg-white/85 backdrop-blur-md" data-testid="site-header-minimal">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" aria-label="newzer.de Startseite"><Logo size={34} textSize="text-xl" /></Link>
        <nav className="flex items-center gap-4 sm:gap-5 text-sm font-medium text-fc-ink/70" aria-label="Rechtliche Navigation">
          <Link to="/" className="hover:text-fc-blue focus-visible:ring-2 focus-visible:ring-fc-blue rounded px-1 py-0.5" data-testid="minimal-nav-home">Zur Startseite</Link>
          <Link to="/datenschutz" className="hover:text-fc-blue focus-visible:ring-2 focus-visible:ring-fc-blue rounded px-1 py-0.5" data-testid="minimal-nav-datenschutz">Datenschutz</Link>
          <Link to="/impressum" className="hover:text-fc-blue focus-visible:ring-2 focus-visible:ring-fc-blue rounded px-1 py-0.5" data-testid="minimal-nav-impressum">Impressum</Link>
        </nav>
      </div>
    </header>
  );
}

export default function SiteHeader({ variant = "full" }) {
  const [open, setOpen] = useState(false); // mobile menu
  const [menu, setMenu] = useState(null); // offener Desktop-Dropdown (Label)
  const navRef = useRef(null);

  useEffect(() => {
    if (variant !== "full") return;
    const onKey = (e) => { if (e.key === "Escape") { setMenu(null); setOpen(false); } };
    const onClick = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setMenu(null); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [variant]);

  if (variant === "minimal") return <MinimalHeader />;

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-fc-border" data-testid="site-header">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between gap-6">
        <Link to="/" aria-label="newzer.de Startseite"><Logo size={34} textSize="text-xl" /></Link>

        <nav ref={navRef} className="hidden lg:flex items-center gap-1" aria-label="Hauptnavigation">
          {Object.entries(NAV).map(([label, items]) => {
            const isOpen = menu === label;
            return (
              <div key={label} className="relative"
                onMouseEnter={() => setMenu(label)} onMouseLeave={() => setMenu((m) => (m === label ? null : m))}>
                <button
                  className="inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-[15px] font-medium text-fc-ink/75 hover:text-fc-ink hover:bg-fc-subtle/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-fc-blue transition-colors"
                  aria-haspopup="true" aria-expanded={isOpen}
                  onClick={() => setMenu(isOpen ? null : label)}
                  data-testid={`nav-menu-${label}`}>
                  {label} <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                <div className={`absolute left-0 top-full pt-2 w-60 transition-all duration-150 ${isOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-1"}`}>
                  <div className="rounded-xl border border-fc-border bg-white shadow-[0_20px_50px_-20px_rgba(19,36,58,0.35)] p-2">
                    {items.map((it) => (
                      <NavItem key={it.label} to={it.to} onClick={() => setMenu(null)}
                        className="block px-3 py-2 rounded-lg text-sm text-fc-ink/75 hover:text-fc-blue hover:bg-fc-subtle/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-fc-blue transition-colors">{it.label}</NavItem>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
          <Link to="/preise" className="px-3.5 py-2 rounded-lg text-[15px] font-medium text-fc-ink/75 hover:text-fc-ink hover:bg-fc-subtle/60 focus-visible:ring-2 focus-visible:ring-fc-blue transition-colors">Preise</Link>
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Link to="/anmelden" className="px-4 py-2 rounded-lg text-[15px] font-semibold text-fc-ink/80 hover:text-fc-blue focus-visible:ring-2 focus-visible:ring-fc-blue transition-colors" data-testid="nav-login">Anmelden</Link>
          <Link to="/registrieren" className="px-5 py-2.5 rounded-xl text-[15px] font-semibold bg-fc-blue text-white hover:bg-fc-blue/90 focus-visible:ring-2 focus-visible:ring-fc-blue transition-colors shadow-sm" data-testid="nav-cta">30 Tage kostenlos testen</Link>
        </div>

        <button className="lg:hidden w-11 h-11 -mr-2 flex items-center justify-center rounded-lg text-fc-ink hover:bg-fc-subtle" onClick={() => setOpen(true)} aria-label="Menü öffnen" data-testid="mobile-menu-open"><Menu className="w-6 h-6" /></button>
      </div>

      {open && createPortal(
        <div className="lg:hidden fixed inset-0 z-[70]" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-fc-ink/40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white shadow-2xl overflow-y-auto">
            <div className="h-[72px] px-5 flex items-center justify-between border-b border-fc-border">
              <Logo size={30} textSize="text-lg" />
              <button className="w-11 h-11 flex items-center justify-center rounded-lg hover:bg-fc-subtle" onClick={() => setOpen(false)} aria-label="Menü schließen" data-testid="mobile-menu-close"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-4 space-y-1">
              {Object.entries(NAV).map(([label, items]) => (
                <details key={label} className="group border-b border-fc-border/60">
                  <summary className="flex items-center justify-between py-3 px-1 font-semibold text-fc-ink cursor-pointer list-none">{label}<ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" /></summary>
                  <div className="pb-2 pl-2 space-y-1">
                    {items.map((it) => <NavItem key={it.label} to={it.to} onClick={() => setOpen(false)} className="block py-2.5 min-h-[44px] flex items-center text-sm text-fc-ink/70">{it.label}</NavItem>)}
                  </div>
                </details>
              ))}
              <Link to="/preise" onClick={() => setOpen(false)} className="block py-3 px-1 font-semibold text-fc-ink border-b border-fc-border/60">Preise</Link>
              <div className="pt-4 space-y-2.5">
                <Link to="/registrieren" onClick={() => setOpen(false)} className="block text-center py-3 rounded-xl font-semibold bg-fc-blue text-white">30 Tage kostenlos testen</Link>
                <Link to="/anmelden" onClick={() => setOpen(false)} className="block text-center py-3 rounded-xl font-semibold border border-fc-border text-fc-ink">Anmelden</Link>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </header>
  );
}
